/**
 * ===================================================================
 * FRONTEND TYPES - HEALTH INCIDENTS
 * ===================================================================
 * Typy pro sledovani zdravotnich problemu rostlin.
 */

/**
 * Typ zdravotniho problemu
 */
export type HealthIncidentType =
  | 'pest' // Skudce (msice, roztoci, atd.)
  | 'disease' // Nemoc (plisne, bakterie, viry)
  | 'nutrient_deficiency' // Deficit zivin
  | 'environmental' // Environmentalni stres (svetlo, teplota)
  | 'mechanical' // Mechanicke poskozeni
  | 'other'; // Ostatni

/**
 * Status incidentu
 */
export type IncidentStatus = 'open' | 'in_treatment' | 'resolved';

/**
 * Zdravotni incident
 */
export interface HealthIncident {
  id: string;

  // Umisteni
  area_id: string;
  slot_id?: string;
  plant_id?: string;
  batch_id?: string;

  // Identifikace problemu
  incident_type: HealthIncidentType;
  name: string;
  species_pest_id?: string;

  // Zavaznost a stav
  severity: 1 | 2 | 3 | 4 | 5;
  status: IncidentStatus;

  // Casove udaje
  observed_at: string;
  resolved_at?: string;

  // Popis
  symptoms?: string;
  notes?: string;

  // Reseni
  treatment_applied?: string;
  treatment_notes?: string;

  // Obrazova dokumentace
  images?: string[];

  // Metadata
  reported_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * DTO pro vytvoreni incidentu
 */
export interface CreateHealthIncidentDTO {
  area_id: string;
  slot_id?: string;
  plant_id?: string;
  batch_id?: string;

  incident_type: HealthIncidentType;
  name: string;
  species_pest_id?: string;

  severity: 1 | 2 | 3 | 4 | 5;

  observed_at?: string;
  symptoms?: string;
  notes?: string;
  images?: string[];
  reported_by?: string;
}

/**
 * DTO pro aktualizaci incidentu
 */
export interface UpdateHealthIncidentDTO {
  incident_type?: HealthIncidentType;
  name?: string;
  severity?: 1 | 2 | 3 | 4 | 5;
  status?: IncidentStatus;

  symptoms?: string;
  notes?: string;

  treatment_applied?: string;
  treatment_notes?: string;
  resolved_at?: string;

  images?: string[];
}

/**
 * Sumar zdravotniho stavu oblasti
 */
export interface AreaHealthSummary {
  area_id: string;
  area_name: string;

  total_incidents: number;
  open_incidents: number;
  in_treatment_incidents: number;
  resolved_incidents: number;

  severity_distribution: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };

  incidents_by_type: Record<HealthIncidentType, number>;

  most_recent_incident?: HealthIncident;
  health_score: number;
}

/**
 * Systemovy prehled zdravi
 */
export interface SystemHealthOverview {
  areas: AreaHealthSummary[];
  overallScore: number;
}

/**
 * Filtry pro vyhledavani incidentu
 */
export interface HealthIncidentFilters {
  status?: IncidentStatus;
  incident_type?: HealthIncidentType;
  severity?: number;
  growing_area_id?: string;
  slot_id?: string;
  from_date?: string;
  to_date?: string;
  severity_min?: number;
  severity_max?: number;
}

// Helper konstanty pro UI
export const INCIDENT_TYPE_LABELS: Record<HealthIncidentType, string> = {
  pest: 'Skudce',
  disease: 'Nemoc',
  nutrient_deficiency: 'Nedostatek zivin',
  environmental: 'Environmentalni stres',
  mechanical: 'Mechanicke poskozeni',
  other: 'Jine',
};

export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
  open: 'Otevren',
  in_treatment: 'V lecbe',
  resolved: 'Vyresen',
};

export const SEVERITY_LABELS: Record<number, string> = {
  1: 'Mirny',
  2: 'Nizky',
  3: 'Stredni',
  4: 'Vysoky',
  5: 'Kriticky',
};

export const SEVERITY_COLORS: Record<number, string> = {
  1: 'green',
  2: 'blue',
  3: 'yellow',
  4: 'orange',
  5: 'red',
};
